import { IMG, FAQS } from "../data/site";
import { PageHero, Section, Overline, Btn } from "../components/site/bits";
import { Reveal, Stagger, StaggerItem } from "../components/site/motion";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Check } from "lucide-react";

const INCLUDED = [
  "Safe, well-maintained accommodation", "Personalised support plan", "Housing & tenancy guidance",
  "Help with benefits & Universal Credit", "Budgeting & financial wellbeing", "ESOL & education referrals",
  "Employment support", "Wellbeing & mental health support", "Community integration activities", "Planned move-on support",
];
const WHO = [
  ["Refugees", "People granted refugee status rebuilding life in the UK."],
  ["Newly recognised refugees", "Support during the critical transition period."],
  ["Migrants", "Those with recourse to public funds needing supported housing."],
  ["Vulnerable adults", "Adults who benefit from care, support or supervision."],
];

export default function Accommodation() {
  return (
    <>
      <PageHero overline="Accommodation" lines={["Supported Exempt", "Accommodation."]} image={IMG.livingRoom}
        intro="Safe, quality accommodation that comes with genuine, personalised support — the foundation for rebuilding an independent life." />

      <Section>
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <Overline>What it is</Overline>
            <Reveal><h2 className="mt-6 font-serif text-4xl tracking-tight text-brand-dark md:text-5xl">More than a place to stay.</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-6 text-lg text-brand-slate">Supported exempt accommodation provides more than minimal care, support or supervision alongside housing. At Shelterease, that means every resident has a safe home and a dedicated support worker helping them work towards independence.</p></Reveal>
          </div>
          <div className="lg:col-span-6"><div className="grid grid-cols-2 gap-4">
            <img src={IMG.livingRoom} alt="Shared living space" className="h-64 w-full rounded-2xl object-cover" />
            <img src={IMG.livingRoom2} alt="Modern accommodation" className="mt-8 h-64 w-full rounded-2xl object-cover" />
          </div></div>
        </div>
      </Section>

      <Section className="bg-brand-surface/60">
        <Overline>Who We Support</Overline>
        <Stagger className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {WHO.map(([t, d]) => (
            <StaggerItem key={t} className="rounded-2xl border border-brand-dark/10 bg-brand-surface p-8">
              <h3 className="font-serif text-xl text-brand-dark">{t}</h3><p className="mt-3 text-sm text-brand-slate">{d}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      <Section>
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Overline>Eligibility</Overline>
            <Reveal><h2 className="mt-6 font-serif text-4xl tracking-tight text-brand-dark md:text-5xl">Who is eligible?</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-6 text-brand-slate">We welcome referrals for refugees, newly recognised refugees, migrants and vulnerable adults who need supported accommodation and are ready to work towards independent living. Each referral is assessed individually against our criteria.</p></Reveal>
            <div className="mt-8"><Btn to="/referrals" testid="accom-referral-btn">Make a referral</Btn></div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Overline>What's Included</Overline>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {INCLUDED.map((i) => (
                <li key={i} className="flex items-start gap-3 text-brand-slate"><Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-green" />{i}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section className="bg-brand-surface/60">
        <div className="mx-auto max-w-3xl">
          <Overline>FAQs</Overline>
          <Reveal><h2 className="mt-6 font-serif text-4xl tracking-tight text-brand-dark md:text-5xl">Frequently asked questions.</h2></Reveal>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-brand-dark/10">
                <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-serif text-xl text-brand-dark hover:text-brand-green hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-base text-brand-slate">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>
    </>
  );
}
